import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  updateDoc, 
  getDoc,
  getDocs,
  addDoc,
  Timestamp,
  orderBy,
  writeBatch,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import ChatService from '../Chat/ChatService';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import { addBusinessDays } from '../utils/dateUtils';

/**
 * Submission Service for Brand Dashboard
 * Handles fetching and managing content submissions from creators
 */
class SubmissionService {
  /**
   * Get projectId from brief's creator assignment
   * @param {string} brandUserId - Brand user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} creatorId - Creator ID
   * @param {string} existingProjectId - Optional existing projectId to validate/match
   * @returns {Promise<string|null>} Project ID or null if not found
   */
  static async getProjectIdFromAssignment(brandUserId, campaignId, briefId, creatorId, existingProjectId = null) {
    try {
      // If we have an existing projectId, validate it first
      if (existingProjectId) {
        const normalized = ProjectIdGenerator.normalizeProjectId(existingProjectId) || existingProjectId;
        // Validate it matches the creator and brief
        try {
          const parsed = ProjectIdGenerator.parseProjectId(normalized);
          // Check if it matches this creator and brief (using first 8 chars)
          if (parsed.creatorId.slice(0, 8) === creatorId.slice(0, 8).padEnd(8, '0') &&
              parsed.briefId.slice(0, 8) === briefId.slice(0, 8).padEnd(8, '0')) {
            return normalized;
          }
        } catch (e) {
          // If parsing fails, continue to fetch from assignment
        }
      }

      // Fetch from brief's creator assignment
      const briefRef = doc(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs', briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) {
        console.warn('Brief not found when fetching projectId');
        return null;
      }
      
      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      
      // Find the creator assignment(s) - there might be multiple assignments for the same creator
      const creatorAssignments = creators.filter(c => c.id === creatorId);
      
      if (creatorAssignments.length === 0) {
        console.warn(`No assignment found for creator ${creatorId} in brief ${briefId}`);
        return null;
      }

      // If we have an existing projectId, try to match it
      if (existingProjectId) {
        const normalized = ProjectIdGenerator.normalizeProjectId(existingProjectId) || existingProjectId;
        const matchingAssignment = creatorAssignments.find(c => {
          const assignmentProjectId = ProjectIdGenerator.normalizeProjectId(c.projectId) || c.projectId;
          return assignmentProjectId === normalized;
        });
        if (matchingAssignment && matchingAssignment.projectId) {
          return ProjectIdGenerator.normalizeProjectId(matchingAssignment.projectId) || matchingAssignment.projectId;
        }
      }

      // If multiple assignments, prefer the most recent one (they have timestamps in projectId)
      // Sort by projectId timestamp (last part of projectId)
      creatorAssignments.sort((a, b) => {
        try {
          const aParsed = ProjectIdGenerator.parseProjectId(a.projectId || '');
          const bParsed = ProjectIdGenerator.parseProjectId(b.projectId || '');
          // Compare timestamps (newer = higher timestamp value)
          return bParsed.timestamp.localeCompare(aParsed.timestamp);
        } catch (e) {
          return 0;
        }
      });

      // Return the most recent assignment's projectId
      const creatorAssignment = creatorAssignments[0];
      if (creatorAssignment && creatorAssignment.projectId) {
        const projectId = ProjectIdGenerator.normalizeProjectId(creatorAssignment.projectId) 
          || creatorAssignment.projectId;
        return projectId;
      }
      
      console.warn(`ProjectId not found for creator ${creatorId} in brief ${briefId}`);
      return null;
    } catch (error) {
      console.error('Error fetching projectId from assignment:', error);
      return null;
    }
  }

  /**
   * Enrich submission with projectId if missing
   * @param {object} submissionData - Submission data
   * @param {string} brandUserId - Brand user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @returns {Promise<object>} Submission data with projectId
   */
  static async enrichSubmissionWithProjectId(submissionData, brandUserId, campaignId, briefId) {
    const creatorId = submissionData.creatorId;
    
    // Always ensure campaignId and briefId are set
    const enriched = {
      ...submissionData,
      campaignId: submissionData.campaignId || campaignId,
      briefId: submissionData.briefId || briefId
    };
    
    if (!creatorId) {
      console.warn('Submission missing creatorId, cannot fetch projectId');
      return enriched;
    }

    // Always read from assignment to ensure correctness (even if submission has projectId)
    // This ensures we match the correct assignment if there are multiple assignments
    const existingProjectId = submissionData.projectId ? 
      (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
    
    const projectId = await this.getProjectIdFromAssignment(
      brandUserId, 
      campaignId, 
      briefId, 
      creatorId,
      existingProjectId // Pass existing to help match if multiple assignments exist
    );
    
    if (projectId) {
      // Update the submission document with projectId if it's missing or incorrect
      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId) || projectId;
      if (!existingProjectId || existingProjectId !== normalizedProjectId) {
        try {
          const submissionId = submissionData.id || submissionData.submissionId;
          if (submissionId) {
            const submissionRef = doc(
              db,
              'users',
              brandUserId,
              'campaigns',
              campaignId,
              'briefs',
              briefId,
              'submissions',
              submissionId
            );
            await updateDoc(submissionRef, { projectId: normalizedProjectId });
          }
        } catch (error) {
          // Non-critical - submission might not exist yet or update might fail
          console.warn('Could not update submission with projectId:', error);
        }
      }
      return { ...enriched, projectId: normalizedProjectId };
    }

    // If we couldn't get projectId from assignment, return submission as-is (or with normalized existing)
    if (existingProjectId) {
      return { ...enriched, projectId: existingProjectId };
    }

    return enriched;
  }
  /**
   * Listen to all submissions for a brand across all campaigns and briefs
   * @param {function} callback - Callback function to receive submissions
   * @returns {function} Unsubscribe function
   */
  static listenToAllSubmissions(callback) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      console.log('Setting up submissions listener for brand:', brandUserId);

      // We need to query all submissions across all campaigns/briefs
      // This requires getting all campaigns first, then all briefs, then all submissions
      // Let's use a different approach: query submissions collection directly if we have it at brand level
      
      // Since submissions are nested under briefs, we need to fetch them hierarchically
      return this.listenToSubmissionsHierarchical(brandUserId, callback);
      
    } catch (error) {
      console.error('Error setting up submissions listener:', error);
      callback([]);
      return () => {};
    }
  }

  /**
   * Hierarchical listener for submissions
   * Fetches campaigns -> briefs -> submissions
   */
  static listenToSubmissionsHierarchical(brandUserId, callback) {
    const unsubscribers = [];
    let allSubmissions = [];
    let submissionListeners = []; // Track submission listeners separately

    // Listen to campaigns
    const campaignsRef = collection(db, 'users', brandUserId, 'campaigns');
    
    const campaignsUnsubscribe = onSnapshot(campaignsRef, async (campaignsSnapshot) => {
      try {
        // Clean up old submission listeners before setting up new ones
        submissionListeners.forEach(unsub => unsub());
        submissionListeners = [];
        allSubmissions = [];
        
        // For each campaign, listen to briefs
        for (const campaignDoc of campaignsSnapshot.docs) {
          const campaignId = campaignDoc.id;
          const campaignData = campaignDoc.data();
          
          const briefsRef = collection(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs');
          const briefsSnapshot = await getDocs(briefsRef);
          
          // For each brief, listen to submissions
          for (const briefDoc of briefsSnapshot.docs) {
            const briefId = briefDoc.id;
            const briefData = briefDoc.data();
            
            const submissionsRef = collection(
              db, 
              'users', 
              brandUserId, 
              'campaigns', 
              campaignId, 
              'briefs', 
              briefId, 
              'submissions'
            );
            
            // Use onSnapshot for real-time updates on submissions
            // Note: onSnapshot fires immediately with existing data, so no need for separate initial fetch
            const submissionsUnsubscribe = onSnapshot(submissionsRef, async (submissionsSnapshot) => {
              // This will fire whenever submissions change (including initial load)
              const submissionPromises = submissionsSnapshot.docs.map(async (submissionDoc) => {
                const submissionData = submissionDoc.data();
                const baseSubmission = {
                  id: submissionDoc.id,
                  ...submissionData,
                  campaignId, // Add campaignId
                  briefId, // Add briefId
                  campaignData,
                  briefData,
                  // Convert Firestore Timestamps to Date objects
                  submittedAt: submissionData.submittedAt?.toDate() || new Date(),
                  createdAt: submissionData.createdAt?.toDate() || new Date(),
                  updatedAt: submissionData.updatedAt?.toDate() || new Date(),
                  dueDate: submissionData.dueDate?.toDate() || null,
                  lastRevisionRequestedAt: submissionData.lastRevisionRequestedAt?.toDate?.() || null,
                  lastResubmittedAt: submissionData.lastResubmittedAt?.toDate?.() || null
                };
                
                // Debug: Log revision submissions
                if (baseSubmission.status === 'revision_submission') {
                  console.log('🔍 Found revision_submission:', {
                    id: baseSubmission.id,
                    status: baseSubmission.status,
                    projectId: baseSubmission.projectId,
                    campaignId,
                    briefId,
                    creatorId: baseSubmission.creatorId
                  });
                }
                
                // Enrich with projectId if missing
                const enrichedSubmission = await this.enrichSubmissionWithProjectId(
                  baseSubmission,
                  brandUserId,
                  campaignId,
                  briefId
                );
                
                return enrichedSubmission;
              });
              
              const enrichedSubmissions = await Promise.all(submissionPromises);
              
              // Remove old submissions for this brief and add new ones
              // Use submission ID for deduplication, not just campaign/brief
              enrichedSubmissions.forEach(newSub => {
                const existingIndex = allSubmissions.findIndex(s => s.id === newSub.id);
                if (existingIndex >= 0) {
                  // Update existing submission
                  allSubmissions[existingIndex] = newSub;
                } else {
                  // Add new submission
                  allSubmissions.push(newSub);
                }
              });
              
              // Remove submissions that no longer exist for this brief
              const existingIds = enrichedSubmissions.map(s => s.id);
              allSubmissions = allSubmissions.filter(s => {
                if (s.campaignId === campaignId && s.briefId === briefId) {
                  return existingIds.includes(s.id);
                }
                return true; // Keep submissions from other briefs
              });
              
              console.log(`Updated submissions for ${campaignData.name}/${briefData.name || briefData.title}: ${enrichedSubmissions.length} submissions (total: ${allSubmissions.length})`);
              
              // Debug: Log all revision submissions
              const revisionSubmissions = allSubmissions.filter(s => s.status === 'revision_submission');
              if (revisionSubmissions.length > 0) {
                console.log('📋 All revision submissions:', revisionSubmissions.map(s => ({
                  id: s.id,
                  status: s.status,
                  projectId: s.projectId,
                  campaignId: s.campaignId,
                  briefId: s.briefId
                })));
              }
              
              callback([...allSubmissions]);
            });
            
            // Track submission listeners separately so we can clean them up when campaigns change
            submissionListeners.push(submissionsUnsubscribe);
            unsubscribers.push(submissionsUnsubscribe);
          }
        }
        
        console.log(`Fetched ${allSubmissions.length} submissions`);
        callback(allSubmissions);
        
      } catch (error) {
        console.error('Error fetching submissions:', error);
        callback([]);
      }
    });

    unsubscribers.push(campaignsUnsubscribe);

    // Return unsubscribe function
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }

  /**
   * Get submissions for a specific campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} Array of submissions
   */
  static async getSubmissionsForCampaign(campaignId) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      const submissions = [];

      // Get all briefs for the campaign
      const briefsRef = collection(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs');
      const briefsSnapshot = await getDocs(briefsRef);

      // For each brief, get submissions
      for (const briefDoc of briefsSnapshot.docs) {
        const briefId = briefDoc.id;
        const briefData = briefDoc.data();

        const submissionsRef = collection(
          db,
          'users',
          brandUserId,
          'campaigns',
          campaignId,
          'briefs',
          briefId,
          'submissions'
        );

        const submissionsSnapshot = await getDocs(submissionsRef);

        // Process submissions and enrich with projectId
        const submissionPromises = submissionsSnapshot.docs.map(async (submissionDoc) => {
          const submissionData = submissionDoc.data();
          const baseSubmission = {
            id: submissionDoc.id,
            ...submissionData,
            briefData,
            campaignId,
            briefId,
            submittedAt: submissionData.submittedAt?.toDate() || new Date(),
            createdAt: submissionData.createdAt?.toDate() || new Date(),
            updatedAt: submissionData.updatedAt?.toDate() || new Date(),
            lastRevisionRequestedAt: submissionData.lastRevisionRequestedAt?.toDate?.() || null,
            lastResubmittedAt: submissionData.lastResubmittedAt?.toDate?.() || null
          };
          
          // Enrich with projectId if missing
          const enrichedSubmission = await this.enrichSubmissionWithProjectId(
            baseSubmission,
            brandUserId,
            campaignId,
            briefId
          );
          
          return enrichedSubmission;
        });
        
        const enrichedSubmissions = await Promise.all(submissionPromises);
        submissions.push(...enrichedSubmissions);
      }

      return submissions;
    } catch (error) {
      console.error('Error getting campaign submissions:', error);
      return [];
    }
  }

  /**
   * Get a specific submission by ID
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} submissionId - Submission ID
   * @returns {Promise<object>} Submission data
   */
  static async getSubmission(campaignId, briefId, submissionId) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      const submissionRef = doc(
        db,
        'users',
        brandUserId,
        'campaigns',
        campaignId,
        'briefs',
        briefId,
        'submissions',
        submissionId
      );

      const submissionDoc = await getDoc(submissionRef);

      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }

      const submissionData = submissionDoc.data();
      const baseSubmission = {
        id: submissionDoc.id,
        ...submissionData,
        campaignId,
        briefId,
        submittedAt: submissionData.submittedAt?.toDate() || new Date(),
        createdAt: submissionData.createdAt?.toDate() || new Date(),
        updatedAt: submissionData.updatedAt?.toDate() || new Date(),
        lastRevisionRequestedAt: submissionData.lastRevisionRequestedAt?.toDate?.() || null,
        lastResubmittedAt: submissionData.lastResubmittedAt?.toDate?.() || null
      };

      // Enrich with projectId if missing
      const enrichedSubmission = await this.enrichSubmissionWithProjectId(
        baseSubmission,
        brandUserId,
        campaignId,
        briefId
      );

      return enrichedSubmission;
    } catch (error) {
      console.error('Error getting submission:', error);
      throw error;
    }
  }

  /**
   * Approve a video in a submission
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} submissionId - Submission ID
   * @param {string} videoId - Video ID to approve
   * @param {object} approvalData - Additional approval data
   * @returns {Promise<boolean>} Success status
   */
  static async approveVideo(campaignId, briefId, submissionId, videoId, approvalData = {}) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      const submissionRef = doc(
        db,
        'users',
        brandUserId,
        'campaigns',
        campaignId,
        'briefs',
        briefId,
        'submissions',
        submissionId
      );

      const submissionDoc = await getDoc(submissionRef);

      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }

      const submissionData = submissionDoc.data();
      const content = submissionData.content || [];

      // Always read projectId from assignment to ensure correctness (handles multiple assignments)
      const projectId = await this.getProjectIdFromAssignment(
        brandUserId, 
        campaignId, 
        briefId, 
        submissionData.creatorId,
        submissionData.projectId || null // Pass existing to help match if multiple assignments exist
      );

      // Update the specific video's status
      const updatedContent = content.map(video => {
        if (video.id === videoId) {
          return {
            ...video,
            status: 'approved',
            approvedAt: new Date().toISOString(),
            approvedBy: brandUserId
          };
        }
        return video;
      });

      // Calculate overall submission status
      const allApproved = updatedContent.every(v => v.status === 'approved');
      const overallStatus = allApproved ? 'approved' : 'partially_approved';

      // Update submission (preserve or store projectId)
      const updateData = {
        content: updatedContent,
        status: overallStatus,
        updatedAt: Timestamp.now()
      };
      // Preserve existing projectId or add new one
      if (submissionData.projectId) {
        updateData.projectId = submissionData.projectId;
      } else if (projectId) {
        updateData.projectId = projectId;
      }
      await updateDoc(submissionRef, updateData);

      // Notify creator
      await this.notifyCreatorOfApproval(
        submissionData.creatorId,
        {
          projectId: projectId,
          campaignId,
          campaignName: submissionData.campaignName,
          briefId,
          briefName: submissionData.briefName,
          submissionId,
          videoTitle: content.find(v => v.id === videoId)?.title,
          allApproved,
          brandUserId: brandUserId
        }
      );

      console.log('Video approved successfully');
      return true;
    } catch (error) {
      console.error('Error approving video:', error);
      throw error;
    }
  }

  /**
   * Approve all videos in a submission
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} submissionId - Submission ID
   * @returns {Promise<{success: boolean, projectId: string, creatorId: string}>} Approval result with projectId and creatorId
   */
  static async approveAllVideos(campaignId, briefId, submissionId) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      const submissionRef = doc(
        db,
        'users',
        brandUserId,
        'campaigns',
        campaignId,
        'briefs',
        briefId,
        'submissions',
        submissionId
      );

      const submissionDoc = await getDoc(submissionRef);

      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }

      const submissionData = submissionDoc.data();
      const content = submissionData.content || [];

      // Always read projectId from assignment to ensure correctness (handles multiple assignments)
      const projectId = await this.getProjectIdFromAssignment(
        brandUserId, 
        campaignId, 
        briefId, 
        submissionData.creatorId,
        submissionData.projectId || null // Pass existing to help match if multiple assignments exist
      );

      // Approve all videos
      const updatedContent = content.map(video => ({
        ...video,
        status: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: brandUserId
      }));

      // Update submission (preserve or store projectId)
      const updateData = {
        content: updatedContent,
        status: 'approved',
        approvedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      // Preserve existing projectId or add new one
      if (submissionData.projectId) {
        updateData.projectId = submissionData.projectId;
      } else if (projectId) {
        updateData.projectId = projectId;
      }
      await updateDoc(submissionRef, updateData);

      // Notify creator
      await this.notifyCreatorOfApproval(
        submissionData.creatorId,
        {
          projectId: projectId,
          campaignId,
          campaignName: submissionData.campaignName,
          briefId,
          briefName: submissionData.briefName,
          submissionId,
          allApproved: true,
          brandUserId: brandUserId
        }
      );

      console.log('All videos approved successfully');
      // Return projectId and creatorId for use in subsequent operations
      return {
        success: true,
        projectId: projectId,
        creatorId: submissionData.creatorId
      };
    } catch (error) {
      console.error('Error approving all videos:', error);
      throw error;
    }
  }

  /**
   * Request revision for specific videos
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} submissionId - Submission ID
   * @param {Array} videosForRevision - Array of { videoId, feedback }
   * @param {string} generalFeedback - General feedback notes
   * @returns {Promise<boolean>} Success status
   */
  static async requestRevision(campaignId, briefId, submissionId, videosForRevision, generalFeedback = '') {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      const submissionRef = doc(
        db,
        'users',
        brandUserId,
        'campaigns',
        campaignId,
        'briefs',
        briefId,
        'submissions',
        submissionId
      );

      const submissionDoc = await getDoc(submissionRef);

      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }

      const submissionData = submissionDoc.data();
      const content = submissionData.content || [];

      // Always read projectId from assignment to ensure correctness (handles multiple assignments)
      const projectId = await this.getProjectIdFromAssignment(
        brandUserId, 
        campaignId, 
        briefId, 
        submissionData.creatorId,
        submissionData.projectId || null // Pass existing to help match if multiple assignments exist
      );

      // Update the specific videos that need revision
      const videoIdsForRevision = videosForRevision.map(v => v.videoId);
      
      const updatedContent = content.map(video => {
        const revisionRequest = videosForRevision.find(v => v.videoId === video.id);
        
        if (revisionRequest) {
          return {
            ...video,
            status: 'needs_revision',
            feedback: revisionRequest.feedback,
            revisionRequestedAt: new Date().toISOString(),
            revisionRequestedBy: brandUserId
          };
        }
        return video;
      });

      // Calculate overall submission status
      const hasRevisionNeeded = updatedContent.some(v => v.status === 'needs_revision');
      const overallStatus = hasRevisionNeeded ? 'needs_revision' : submissionData.status;
      
      console.log('🔍 Revision Request - Status Calculation:', {
        hasRevisionNeeded,
        overallStatus,
        originalStatus: submissionData.status,
        videosNeedingRevision: updatedContent.filter(v => v.status === 'needs_revision').length,
        totalVideos: updatedContent.length,
        updatedContentStatuses: updatedContent.map(v => ({ id: v.id, status: v.status }))
      });

      // Don't increment revision count when requesting revision - only when revision is submitted
      const revisionCount = submissionData.revisionCount || 0;

      // Check if revision limit exceeded (3 revision requests)
      // We need to track revision requests separately from submitted revisions
      const revisionRequestCount = (submissionData.revisionRequestCount || 0) + 1;
      if (revisionRequestCount >= 3) {
        // Auto-reject after 3 revision requests (also store projectId if not present)
        const rejectUpdateData = {
          content: updatedContent,
          status: 'rejected',
          rejectedAt: Timestamp.now(),
          rejectionReason: 'Exceeded revision limit (3 requests)',
          revisionCount,
          revisionRequestCount,
          updatedAt: Timestamp.now()
        };
        if (projectId && !submissionData.projectId) {
          rejectUpdateData.projectId = projectId;
        }
        await updateDoc(submissionRef, rejectUpdateData);

        // Notify creator of rejection
        await this.notifyCreatorOfRejection(
          submissionData.creatorId,
          {
            projectId: projectId,
            campaignId,
            campaignName: submissionData.campaignName,
            briefId,
            briefName: submissionData.briefName,
            submissionId,
            reason: 'Exceeded revision limit (3 requests)'
          }
        );

        return true;
      }

      // Update submission with revision request (preserve or store projectId)
      const updateData = {
        content: updatedContent,
        status: overallStatus,
        revisionCount,
        revisionRequestCount,
        generalFeedback,
        lastRevisionRequestedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      // Preserve existing projectId or add new one
      if (submissionData.projectId) {
        updateData.projectId = submissionData.projectId;
      } else if (projectId) {
        updateData.projectId = projectId;
      }
      await updateDoc(submissionRef, updateData);

      // Notify creator
      await this.notifyCreatorOfRevisionRequest(
        submissionData.creatorId,
        {
          projectId: projectId,
          campaignId,
          campaignName: submissionData.campaignName,
          briefId,
          briefName: submissionData.briefName,
          submissionId,
          videosForRevision,
          generalFeedback,
          revisionCount,
          revisionRequestCount
        }
      );

      console.log('Revision requested successfully');
      return true;
    } catch (error) {
      console.error('Error requesting revision:', error);
      throw error;
    }
  }

  /**
   * Reject a submission
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} submissionId - Submission ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<boolean>} Success status
   */
  static async rejectSubmission(campaignId, briefId, submissionId, reason = '') {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      const submissionRef = doc(
        db,
        'users',
        brandUserId,
        'campaigns',
        campaignId,
        'briefs',
        briefId,
        'submissions',
        submissionId
      );

      const submissionDoc = await getDoc(submissionRef);

      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }

      const submissionData = submissionDoc.data();

      // Always read projectId from assignment to ensure correctness (handles multiple assignments)
      const projectId = await this.getProjectIdFromAssignment(
        brandUserId, 
        campaignId, 
        briefId, 
        submissionData.creatorId,
        submissionData.projectId || null // Pass existing to help match if multiple assignments exist
      );

      // Update submission status to rejected (preserve or store projectId)
      const updateData = {
        status: 'rejected',
        rejectedAt: Timestamp.now(),
        rejectedBy: brandUserId,
        rejectionReason: reason,
        updatedAt: Timestamp.now()
      };
      // Preserve existing projectId or add new one
      if (submissionData.projectId) {
        updateData.projectId = submissionData.projectId;
      } else if (projectId) {
        updateData.projectId = projectId;
      }
      await updateDoc(submissionRef, updateData);

      // Notify creator
      await this.notifyCreatorOfRejection(
        submissionData.creatorId,
        {
          projectId: projectId,
          campaignId,
          campaignName: submissionData.campaignName,
          briefId,
          briefName: submissionData.briefName,
          submissionId,
          reason
        }
      );

      console.log('Submission rejected');
      return true;
    } catch (error) {
      console.error('Error rejecting submission:', error);
      throw error;
    }
  }

  /**
   * Notify creator of approval
   */
  static async notifyCreatorOfApproval(creatorId, approvalData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;

      // Require projectId from the assignment; never generate here
      const projectId = approvalData.projectId || null;

      const notification = {
        type: 'content_approved',
        title: 'Content Approved',
        message: approvalData.allApproved
          ? `All your videos for "${approvalData.briefName}" have been approved!`
          : `Your video "${approvalData.videoTitle}" for "${approvalData.briefName}" has been approved!`,
        // REQUIRED fields for notification routing and security
        recipientId: creatorId,
        creatorId: creatorId,
        projectId: projectId,
        briefId: approvalData.briefId,
        campaignId: approvalData.campaignId,
        details: {
          projectId: projectId,
          campaignId: approvalData.campaignId,
          campaignName: approvalData.campaignName,
          briefId: approvalData.briefId,
          briefName: approvalData.briefName,
          submissionId: approvalData.submissionId,
          allApproved: approvalData.allApproved,
          approvedAt: new Date().toISOString(),
          brandUserId: brandUserId
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const notificationsRef = collection(db, 'creators', creatorId, 'notifications');
      await addDoc(notificationsRef, notification);

      console.log('Creator notified of approval');
    } catch (error) {
      console.error('Error notifying creator of approval:', error);
      // Don't throw - notification failure shouldn't block approval
    }
  }

  /**
   * Notify creator of revision request
   */
  static async notifyCreatorOfRevisionRequest(creatorId, revisionData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      // Require projectId from the assignment; never generate here
      const projectId = revisionData.projectId || null;

      const notification = {
        type: 'revision_requested',
        title: 'Revision Requested',
        message: `The brand requested revisions for "${revisionData.briefName}" (Request ${revisionData.revisionRequestCount || 1}/2)`,
        // REQUIRED fields for notification routing and security
        recipientId: creatorId,
        creatorId: creatorId,
        projectId: projectId,
        briefId: revisionData.briefId,
        campaignId: revisionData.campaignId,
        details: {
          projectId: projectId,
          campaignId: revisionData.campaignId,
          campaignName: revisionData.campaignName,
          briefId: revisionData.briefId,
          briefName: revisionData.briefName,
          submissionId: revisionData.submissionId,
          brandUserId: brandUserId, // Add brand user ID for creator dashboard
          videosForRevision: revisionData.videosForRevision,
          generalFeedback: revisionData.generalFeedback,
          revisionCount: revisionData.revisionCount,
          revisionRequestCount: revisionData.revisionRequestCount,
          requestedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const notificationsRef = collection(db, 'creators', creatorId, 'notifications');
      await addDoc(notificationsRef, notification);

      console.log('Creator notified of revision request');
    } catch (error) {
      console.error('Error notifying creator of revision:', error);
      // Don't throw - notification failure shouldn't block revision request
    }
  }

  /**
   * Notify creator of rejection
   */
  static async notifyCreatorOfRejection(creatorId, rejectionData) {
    try {
      // Require projectId from the assignment; never generate here
      const projectId = rejectionData.projectId || null;
      const brandUserId = getAuth().currentUser?.uid || null;

      const notification = {
        type: 'content_rejected',
        title: 'Content Rejected',
        message: `Your submission for "${rejectionData.briefName}" was rejected`,
        // REQUIRED fields for notification routing and security
        recipientId: creatorId,
        creatorId: creatorId,
        projectId: projectId,
        briefId: rejectionData.briefId,
        campaignId: rejectionData.campaignId,
        details: {
          projectId: projectId,
          brandUserId: brandUserId,
          campaignId: rejectionData.campaignId,
          campaignName: rejectionData.campaignName,
          briefId: rejectionData.briefId,
          briefName: rejectionData.briefName,
          submissionId: rejectionData.submissionId,
          reason: rejectionData.reason,
          rejectedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const notificationsRef = collection(db, 'creators', creatorId, 'notifications');
      await addDoc(notificationsRef, notification);

      console.log('Creator notified of rejection');
    } catch (error) {
      console.error('Error notifying creator of rejection:', error);
      // Don't throw - notification failure shouldn't block rejection
    }
  }

  /**
   * Complete a project and move it to completed status
   */
  static async completeProject(campaignId, briefId, creatorId, projectId = null, submissionId = null) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;

      // Update the brief's creator assignment status to completed
      const briefRef = doc(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs', briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) {
        throw new Error('Brief not found');
      }

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      
      // Get projectId from assignment if not provided
      let normalizedProjectId = projectId ? ProjectIdGenerator.normalizeProjectId(projectId) : null;
      if (!normalizedProjectId) {
        normalizedProjectId = await this.getProjectIdFromAssignment(
          brandUserId,
          campaignId,
          briefId,
          creatorId,
          null
        );
      }

      // If submissionId not provided, try to find it from submissions
      let finalSubmissionId = submissionId;
      if (!finalSubmissionId) {
        const submissionsRef = collection(
          db,
          'users',
          brandUserId,
          'campaigns',
          campaignId,
          'briefs',
          briefId,
          'submissions'
        );
        const submissionsSnapshot = await getDocs(submissionsRef);
        
        // Find submission with matching projectId
        for (const submissionDoc of submissionsSnapshot.docs) {
          const submissionData = submissionDoc.data();
          const submissionProjectId = submissionData.projectId
            ? ProjectIdGenerator.normalizeProjectId(submissionData.projectId)
            : null;
          
          if (submissionProjectId && normalizedProjectId && 
              ProjectIdGenerator.areEquivalent(submissionProjectId, normalizedProjectId)) {
            finalSubmissionId = submissionDoc.id;
            break;
          }
        }
      }

      // Update the creator's status in the brief
      const updatedCreators = creators.map(c => {
        const matchesCreator = c.id === creatorId;
        const matchesProject = normalizedProjectId && c.projectId
          ? ProjectIdGenerator.areEquivalent(c.projectId, normalizedProjectId)
          : true; // If no projectId, match by creator only
        
        if (matchesCreator && matchesProject) {
          return {
            ...c,
            status: 'completed',
            completedAt: new Date().toISOString(),
            submissionId: finalSubmissionId, // Store submissionId for archiving
            // Remove delivery address for privacy when completed
            deliveryAddress: null,
            shippingDetails: null
          };
        }
        return c;
      });

      await updateDoc(briefRef, {
        creators: updatedCreators,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Project marked as completed in brief', {
        projectId: normalizedProjectId,
        submissionId: finalSubmissionId
      });

      // Write to creator-owned completed collection to avoid scanning brand data
      // This ensures creators only read from their own data, eliminating cross-contamination bugs
      try {
        const creatorAssignment = updatedCreators.find(c => {
          const matchesCreator = c.id === creatorId;
          const creatorProjectId = c.projectId || c.progress?.projectId || null;
          const matchesProject = creatorProjectId && normalizedProjectId
            ? ProjectIdGenerator.areEquivalent(creatorProjectId, normalizedProjectId)
            : false;
          return matchesCreator && matchesProject;
        });

        let brandDisplayName = briefData.brandName || null;
        try {
          const brandProfileSnap = await getDoc(doc(db, 'users', brandUserId));
          if (brandProfileSnap.exists()) {
            const b = brandProfileSnap.data() || {};
            brandDisplayName =
              b.displayName || b.brandName || b.companyName || b.agencyName || b.name || brandDisplayName;
          }
        } catch (_) {
          // non-critical
        }

        const completedProject = {
          projectId: normalizedProjectId,
          creatorId: creatorId,
          status: 'completed',
          completedAt: new Date().toISOString(),
          approvedAt: new Date().toISOString(),
          brandUserId: brandUserId,
          campaignId: campaignId,
          campaignName: briefData.campaignName || briefData.name || campaignId,
          brandName: brandDisplayName,
          briefId: briefId,
          briefTitle: briefData.title || briefData.name,
          briefName: briefData.title || briefData.name,
          submissionId: finalSubmissionId,
          totalPrice: creatorAssignment?.totalPrice || 0,
          brandReview: creatorAssignment?.brandReview || null,
          scriptIds: creatorAssignment?.scriptIds || [],
          productType: briefData.productType || creatorAssignment?.productType || 'service',
          assignedAt: creatorAssignment?.assignedAt || null,
          acceptedAt: creatorAssignment?.acceptedAt || null,
          deadline: creatorAssignment?.deadline || null,
          tier: creatorAssignment?.tier || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const completedRef = doc(db, 'creators', creatorId, 'completed', normalizedProjectId);
        await setDoc(completedRef, completedProject, { merge: true });

        console.log('✅ Completed project written to creator-owned collection:', {
          creatorId,
          projectId: normalizedProjectId
        });
      } catch (writeError) {
        // Non-critical - log but don't block completion
        console.warn('⚠️ Could not write to creator-owned completed collection:', writeError);
      }

      // Check if all briefs in the campaign are completed
      await this.checkCampaignCompletion(campaignId, brandUserId);

    } catch (error) {
      console.error('❌ Error completing project:', error);
      throw error;
    }
  }

  /**
   * Check if all briefs in a campaign are completed and mark campaign as complete if so
   */
  static async checkCampaignCompletion(campaignId, brandUserId) {
    try {
      // Get all briefs for this campaign
      const briefsRef = collection(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs');
      const briefsSnapshot = await getDocs(briefsRef);
      
      if (briefsSnapshot.empty) {
        console.log('No briefs found for campaign:', campaignId);
        return;
      }

      const briefs = briefsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log(`🔍 Checking campaign completion for ${briefs.length} briefs`);

      // Check each brief to see if all assigned creators have completed their work
      let allBriefsCompleted = true;
      
      for (const brief of briefs) {
        const briefCreators = brief.creators || [];
        console.log(`📋 Brief "${brief.title || brief.id}" has ${briefCreators.length} creators`);
        
        if (briefCreators.length === 0) {
          // If no creators assigned, brief is not completed
          allBriefsCompleted = false;
          console.log(`❌ Brief "${brief.title || brief.id}" has no creators assigned`);
          break;
        }

        // Check if all creators for this brief have completed their assignments
        for (const briefCreator of briefCreators) {
          const assignmentRef = doc(db, 'users', briefCreator.id, 'assignments', brief.id);
          const assignmentDoc = await getDoc(assignmentRef);
          
          if (!assignmentDoc.exists()) {
            console.log(`❌ No assignment found for creator ${briefCreator.handle} in brief ${brief.id}`);
            allBriefsCompleted = false;
            break;
          }
          
          const assignmentData = assignmentDoc.data();
          if (assignmentData.status !== 'completed') {
            console.log(`❌ Creator ${briefCreator.handle} has not completed brief ${brief.id} (status: ${assignmentData.status})`);
            allBriefsCompleted = false;
            break;
          }
        }
        
        if (!allBriefsCompleted) {
          break;
        }
      }

      if (allBriefsCompleted) {
        console.log('🎉 All briefs completed! Marking campaign as complete');
        
        // Mark campaign as completed
        const campaignRef = doc(db, 'users', brandUserId, 'campaigns', campaignId);
        await updateDoc(campaignRef, {
          status: 'completed',
          completedAt: new Date().toISOString()
        });

        // Send completion notification to brand
        const brandNotification = {
          type: 'campaign_completed',
          title: 'Campaign Completed',
          message: `All briefs in your campaign have been completed successfully!`,
          details: {
            campaignId,
            completedAt: new Date().toISOString(),
            totalBriefs: briefs.length
          },
          read: false,
          createdAt: new Date().toISOString()
        };

        const brandNotificationsRef = collection(db, 'users', brandUserId, 'notifications');
        await addDoc(brandNotificationsRef, brandNotification);

        console.log('✅ Campaign marked as completed');
      } else {
        console.log('⏳ Campaign not yet complete - some briefs still in progress');
      }

    } catch (error) {
      console.error('❌ Error checking campaign completion:', error);
      // Don't throw - this shouldn't block the individual project completion
    }
  }

  /**
   * Close chat and send completion message
   */
  static async closeChatWithCompletionMessage(campaignId, briefId, creatorId, projectId = null) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      const completionMessage = "Project completed successfully! Chat closed.";

      // Send completion message to creator
      const creatorNotification = {
        type: 'project_completed',
        title: 'Project Completed',
        message: completionMessage,
        details: {
          campaignId,
          briefId,
          brandUserId,
          completedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const creatorNotificationsRef = collection(db, 'users', creatorId, 'notifications');
      await addDoc(creatorNotificationsRef, creatorNotification);

      // Send completion message to brand
      const brandNotification = {
        type: 'project_completed',
        title: 'Project Completed',
        message: completionMessage,
        details: {
          campaignId,
          briefId,
          creatorId,
          completedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const brandNotificationsRef = collection(db, 'users', brandUserId, 'notifications');
      await addDoc(brandNotificationsRef, brandNotification);

      if (projectId) {
        try {
          await ChatService.closeChat(projectId);
          console.log('✅ Project chat closed for project:', projectId);
        } catch (chatError) {
          console.error('❌ Error closing project chat:', chatError);
        }
      }

      console.log('✅ Chat closed with completion message');
    } catch (error) {
      console.error('❌ Error closing chat:', error);
      throw error;
    }
  }

  /**
   * Submit a review for a completed project
   * Uses projectId to find submission and creator assignment (avoids duplicate issues)
   * @param {object} submission - Submission object with projectId
   * @param {object} reviewData - Review data with review text
   */
  static async submitReview(submission, reviewData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const brandUserId = user.uid;
      
      // Get projectId - this is the key identifier
      const projectId = submission.projectId || submission._originalData?.projectId;
      
      if (!projectId) {
        console.error('Missing projectId:', { submission });
        throw new Error('Cannot submit review: Missing projectId. The submission must have a projectId.');
      }

      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId) || projectId;

      console.log('📝 Submitting review for projectId:', normalizedProjectId);

      // Find the submission by projectId across all campaigns/briefs
      // We need to search through all campaigns to find the submission
      const campaignsRef = collection(db, 'users', brandUserId, 'campaigns');
      const campaignsSnapshot = await getDocs(campaignsRef);
      
      let foundSubmission = null;
      let foundCampaignId = null;
      let foundBriefId = null;
      let foundSubmissionId = null;
      let foundCreatorId = null;

      // Search through all campaigns and briefs to find the submission with matching projectId
      for (const campaignDoc of campaignsSnapshot.docs) {
        const campaignId = campaignDoc.id;
        const briefsRef = collection(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs');
        const briefsSnapshot = await getDocs(briefsRef);

        for (const briefDoc of briefsSnapshot.docs) {
          const briefId = briefDoc.id;
          const submissionsRef = collection(
            db,
            'users',
            brandUserId,
            'campaigns',
            campaignId,
            'briefs',
            briefId,
            'submissions'
          );
          
          const submissionsSnapshot = await getDocs(submissionsRef);
          
          // Find submission with matching projectId
          for (const submissionDoc of submissionsSnapshot.docs) {
            const submissionData = submissionDoc.data();
            const submissionProjectId = submissionData.projectId ? 
              (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
            
            if (submissionProjectId && (
              submissionProjectId === normalizedProjectId ||
              ProjectIdGenerator.areEquivalent(submissionProjectId, normalizedProjectId)
            )) {
              foundSubmission = submissionData;
              foundCampaignId = campaignId;
              foundBriefId = briefId;
              foundSubmissionId = submissionDoc.id;
              foundCreatorId = submissionData.creatorId;
              break;
            }
          }
          
          if (foundSubmission) break;
        }
        
        if (foundSubmission) break;
      }

      if (!foundSubmission || !foundCampaignId || !foundBriefId || !foundSubmissionId) {
        throw new Error(`Cannot submit review: Submission not found for projectId ${normalizedProjectId}`);
      }

      // Prepare review object - structure it to match creator dashboard expectations
      // The creator dashboard expects: communication, quality, timeliness, wouldWorkAgain, additionalComments
      // For now, we'll map the simple text review to additionalComments
      const reviewText = reviewData.review || reviewData.reviewText || '';
      const brandReview = {
        // Structured fields (optional - can be added later with a more detailed review form)
        communication: reviewData.communication || null,
        quality: reviewData.quality || null,
        timeliness: reviewData.timeliness || null,
        wouldWorkAgain: reviewData.wouldWorkAgain || null,
        // Main review text goes to additionalComments so it displays on creator dashboard
        additionalComments: reviewText,
        // Metadata
        submittedBy: brandUserId,
        submittedAt: new Date().toISOString(),
        timestamp: Timestamp.now()
      };

      // 1. Update the submission document with the review (using found IDs)
      const submissionRef = doc(
        db,
        'users',
        brandUserId,
        'campaigns',
        foundCampaignId,
        'briefs',
        foundBriefId,
        'submissions',
        foundSubmissionId
      );
      
      await updateDoc(submissionRef, {
        brandReview: brandReview
      });

      // 2. Update the creator's assignment in the brief with the review
      // Use projectId to find the correct assignment (handles duplicates)
      if (foundCreatorId) {
        const briefRef = doc(db, 'users', brandUserId, 'campaigns', foundCampaignId, 'briefs', foundBriefId);
        const briefDoc = await getDoc(briefRef);
        
        if (briefDoc.exists()) {
          const briefData = briefDoc.data();
          const creators = briefData.creators || [];
          
          // Find and update the creator assignment with matching projectId
          const updatedCreators = creators.map(creator => {
            const creatorProjectId = creator.projectId ? 
              (ProjectIdGenerator.normalizeProjectId(creator.projectId) || creator.projectId) : null;
            
            // Match by projectId to ensure we update the correct assignment (handles duplicates)
            if (creator.id === foundCreatorId && creatorProjectId && (
              creatorProjectId === normalizedProjectId || 
              ProjectIdGenerator.areEquivalent(creatorProjectId, normalizedProjectId)
            )) {
              return {
                ...creator,
                brandReview: brandReview
              };
            }
            return creator;
          });

          // Update the brief with the modified creators array
          await updateDoc(briefRef, {
            creators: updatedCreators
          });
        }
      }

      console.log('✅ Review submitted successfully and synced to creator assignment');
    } catch (error) {
      console.error('❌ Error submitting review:', error);
      throw error;
    }
  }

  /**
   * Auto-approve submissions that are overdue (submitted more than 5 business days ago)
   * This should be called periodically or on page load
   */
  static async autoApproveOverdueSubmissions() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.warn('⚠️ User not authenticated, skipping auto-approval');
        return;
      }

      const brandUserId = user.uid;
      const now = new Date();
      
      console.log('🔍 Checking for overdue submissions to auto-approve...');

      // Get all campaigns
      const campaignsRef = collection(db, 'users', brandUserId, 'campaigns');
      const campaignsSnapshot = await getDocs(campaignsRef);
      
      let autoApprovedCount = 0;

      for (const campaignDoc of campaignsSnapshot.docs) {
        const campaignId = campaignDoc.id;
        
        // Get all briefs in this campaign
        const briefsRef = collection(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs');
        const briefsSnapshot = await getDocs(briefsRef);
        
        for (const briefDoc of briefsSnapshot.docs) {
          const briefId = briefDoc.id;
          
          // Get all submissions with status='submitted' OR 'revision_submission' (for final revision 3/3)
          const submissionsRef = collection(
            db,
            'users',
            brandUserId,
            'campaigns',
            campaignId,
            'briefs',
            briefId,
            'submissions'
          );
          
          // Query for both 'submitted' and 'revision_submission' statuses
          const submissionsQuery = query(
            submissionsRef,
            where('status', 'in', ['submitted', 'revision_submission'])
          );
          
          const submissionsSnapshot = await getDocs(submissionsQuery);
          
          for (const submissionDoc of submissionsSnapshot.docs) {
            const submissionData = submissionDoc.data();
            const submissionId = submissionDoc.id;
            const revisionCount = submissionData.revisionCount || 0;
            const isFinalRevision = revisionCount >= 2; // revisionCount 2 = Revision 3/3 (final revision)
            
            // For revision_submission status, only auto-approve if it's the final revision (3/3)
            // For regular 'submitted' status, use the existing logic (auto-approve all)
            if (submissionData.status === 'revision_submission' && !isFinalRevision) {
              // Not final revision, skip auto-approval (let brand review it)
              continue;
            }
            
            // For final revision (3/3) with revision_submission status, proceed to auto-approve if overdue
            // For regular 'submitted' status, proceed to auto-approve if overdue
            
            // Get submittedAt or lastResubmittedAt date
            let submittedAt = submissionData.submittedAt?.toDate ? 
              submissionData.submittedAt.toDate() : 
              (submissionData.submittedAt instanceof Date ? 
                submissionData.submittedAt : 
                (submissionData.submittedAt ? new Date(submissionData.submittedAt) : null));
            
            // For revision submissions, use lastResubmittedAt if available
            if (submissionData.status === 'revision_submission' && submissionData.lastResubmittedAt) {
              submittedAt = submissionData.lastResubmittedAt?.toDate ? 
                submissionData.lastResubmittedAt.toDate() : 
                (submissionData.lastResubmittedAt instanceof Date ? 
                  submissionData.lastResubmittedAt : 
                  (submissionData.lastResubmittedAt ? new Date(submissionData.lastResubmittedAt) : null));
            }
            
            if (!submittedAt) {
              console.warn(`⚠️ Submission ${submissionId} missing submittedAt/lastResubmittedAt, skipping`);
              continue;
            }
            
            // Respect an explicit review deadline when set (e.g. admin extension for client delay).
            const storedDueDate = submissionData.dueDate?.toDate
              ? submissionData.dueDate.toDate()
              : submissionData.dueDate instanceof Date
                ? submissionData.dueDate
                : submissionData.dueDate
                  ? new Date(submissionData.dueDate)
                  : null;
            const reviewDeadline = storedDueDate || addBusinessDays(submittedAt, 5);
            if (now > reviewDeadline) {
              const daysAgo = Math.floor((now.getTime() - submittedAt.getTime()) / (1000 * 60 * 60 * 24));
              console.log(`⏰ Auto-approving overdue ${isFinalRevision ? 'final revision (3/3)' : 'submission'}: ${submissionId} (submitted ${daysAgo} days ago, revisionCount: ${revisionCount})`);
              
              try {
                // Auto-approve the submission (returns projectId and creatorId)
                const approvalResult = await this.approveAllVideos(campaignId, briefId, submissionId);
                
                // Use the projectId returned from approveAllVideos (ensures consistency)
                const projectId = approvalResult.projectId;
                const creatorId = approvalResult.creatorId || submissionData.creatorId;
                
                if (!projectId) {
                  console.warn(`⚠️ No projectId returned from approval for submission ${submissionId}, skipping completion`);
                  continue;
                }
                
                // Complete the project using the same projectId
                await this.completeProject(campaignId, briefId, creatorId, projectId);
                
                // Close chat with completion message using the same projectId
                await this.closeChatWithCompletionMessage(
                  campaignId,
                  briefId,
                  creatorId,
                  projectId
                );
                
                autoApprovedCount++;
                console.log(`✅ Auto-approved ${isFinalRevision ? 'final revision (3/3)' : 'submission'}: ${submissionId}`);
              } catch (error) {
                console.error(`❌ Error auto-approving submission ${submissionId}:`, error);
                // Continue with other submissions even if one fails
              }
            }
          }
        }
      }
      
      if (autoApprovedCount > 0) {
        console.log(`✅ Auto-approved ${autoApprovedCount} overdue submission(s)`);
      } else {
        console.log('✅ No overdue submissions found');
      }
    } catch (error) {
      console.error('❌ Error in auto-approval process:', error);
      // Don't throw - this is a background process
    }
  }
}

export default SubmissionService;

